import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, UploadCloud } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useSEO } from "@/components/seo";
import { Reveal, MaskedLine, Marquee, SectionHead } from "@/components/motion";

const HERO_IMG = "https://images.unsplash.com/photo-1740209475472-aa7d280f7452?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwxfHxDTkMlMjBtaWxsaW5nJTIwbWV0YWwlMjBwcmVjaXNpb24lMjBwYXJ0cyUyMG1hY2hpbmluZ3xlbnwwfHx8fDE3ODgzNDIyMDd8MA&ixlib=rb-4.1.0&q=85";
const ABOUT_IMG = "https://images.unsplash.com/photo-1666634157070-6fd830fb5672?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwyfHxDTkMlMjBtaWxsaW5nJTIwbWV0YWwlMjBwcmVjaXNpb24lMjBwYXJ0cyUyMG1hY2hpbmluZ3xlbnwwfHx8fDE3ODgzNDIyMDd8MA&ixlib=rb-4.1.0&q=85";
const GEAR_IMG = "https://images.unsplash.com/photo-1759621165667-da064b86fdd0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwzfHxtZXRhbCUyMGVuZ2luZWVyaW5nJTIwZ2VhciUyMHByZWNpc2lvbiUyMG1lY2hhbmljYWwlMjBlbmdpbmVlcmluZ3xlbnwwfHx8fDE3ODgzNDIyMTR8MA&ixlib=rb-4.1.0&q=85";

export default function Home() {
  const { t, pathFor } = useLang();
  useSEO("home");
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative flex min-h-screen flex-col overflow-hidden" data-testid="hero-section">
        <motion.div className="absolute inset-0" style={{ y: bgY }}>
          <img src={HERO_IMG} alt="CNC-Bearbeitung eines Metallbauteils in der Fertigung von R-Mechanik" className="h-[120%] w-full object-cover" />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-b from-anthracite-dark/80 via-anthracite-dark/70 to-anthracite-dark" />
        <div className="absolute inset-0 grid-lines-dark" />

        <div className="relative mx-auto flex w-full max-w-7xl flex-1 flex-col justify-center px-4 pt-28 sm:px-8">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="font-mono-tech text-xs uppercase tracking-[0.35em] text-royal-soft"
            data-testid="hero-kicker"
          >
            {t.hero.kicker}
          </motion.p>
          <h1 className="mt-6 text-4xl font-black uppercase leading-[0.95] tracking-tight text-white sm:text-5xl lg:text-7xl" data-testid="hero-heading">
            <MaskedLine delay={0.25}>{t.hero.line1}</MaskedLine>
            <MaskedLine delay={0.38}>{t.hero.line2}</MaskedLine>
            <MaskedLine delay={0.51} className="text-royal-soft">{t.hero.line3}</MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="mt-6 max-w-xl text-base text-slate-300 sm:text-lg"
            data-testid="hero-sub"
          >
            {t.hero.sub}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1.05 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <Link
              to={pathFor("inquiry")}
              className="group flex items-center gap-3 bg-royal px-7 py-4 text-sm font-bold uppercase tracking-wider text-white transition-colors duration-200 hover:bg-royal-hover"
              data-testid="hero-cta-inquiry"
            >
              <UploadCloud size={18} className="transition-transform duration-200 group-hover:-translate-y-0.5" />
              {t.hero.cta1}
            </Link>
            <Link
              to={pathFor("services")}
              className="group flex items-center gap-3 border border-white/25 px-7 py-4 text-sm font-bold uppercase tracking-wider text-white transition-colors duration-200 hover:border-royal-soft hover:text-royal-soft"
              data-testid="hero-cta-services"
            >
              {t.hero.cta2}
              <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.25 }}
          className="relative border-t border-white/10"
        >
          <div className="mx-auto grid max-w-7xl grid-cols-2 divide-x divide-white/10 lg:grid-cols-4">
            {t.hero.metrics.map((m, i) => (
              <div key={m} className="flex items-center gap-3 px-4 py-5 sm:px-6" data-testid={`hero-metric-${i}`}>
                <span className="font-mono-tech text-xs text-royal-soft">0{i + 1}</span>
                <span className="text-xs font-medium uppercase tracking-wide text-slate-300 sm:text-sm">{m}</span>
              </div>
            ))}
          </div>
        </motion.div>
      </section>

      <Marquee items={t.marquee} />

      {/* ADVANTAGES */}
      <section className="bg-slate-50 grid-lines-light" data-testid="advantages-section">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-8 lg:py-28">
          <SectionHead kicker={t.advantages.kicker} title={t.advantages.title} testid="advantages" />
          <div className="mt-14 grid gap-px border border-slate-200 bg-slate-200 sm:grid-cols-2 lg:grid-cols-3">
            {t.advantages.items.map((a, i) => (
              <Reveal key={a.title} delay={i * 0.06} className="h-full">
                <div className="group flex h-full flex-col bg-white p-8 transition-colors duration-300 hover:bg-anthracite" data-testid={`advantage-card-${i}`}>
                  <span className="font-mono-tech text-sm text-royal transition-colors group-hover:text-royal-soft">0{i + 1}</span>
                  <h3 className="mt-4 text-lg font-bold text-slate-900 transition-colors group-hover:text-white">{a.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-500 transition-colors group-hover:text-slate-300">{a.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT TEASER */}
      <section className="bg-anthracite-dark" data-testid="home-about-section">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 py-20 sm:px-8 lg:grid-cols-2 lg:py-28">
          <Reveal className="relative order-2 lg:order-1">
            <div className="clip-frame relative overflow-hidden">
              <img src={ABOUT_IMG} alt="CNC-Drehmaschine bei der Bearbeitung eines Drehteils" className="h-full min-h-[320px] w-full object-cover transition-transform duration-700 hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-anthracite-dark/60 to-transparent" />
            </div>
            <div className="absolute -bottom-4 -right-4 hidden border border-royal/40 p-6 lg:block" aria-hidden="true" />
          </Reveal>
          <div className="order-1 flex flex-col justify-center lg:order-2">
            <SectionHead kicker={t.home.aboutKicker} title={t.home.aboutTitle} light testid="home-about" />
            <Reveal delay={0.1}>
              <p className="mt-6 max-w-xl leading-relaxed text-slate-400">{t.home.aboutText}</p>
              <Link to={pathFor("about")} className="group mt-8 inline-flex items-center gap-3 font-mono-tech text-sm uppercase tracking-widest text-royal-soft transition-colors hover:text-white" data-testid="home-about-link">
                {t.home.aboutLink}
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* SERVICES OVERVIEW */}
      <section className="bg-slate-50 grid-lines-light" data-testid="home-services-section">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-8 lg:py-28">
          <SectionHead kicker={t.home.servicesKicker} title={t.home.servicesTitle} testid="home-services" />
          <div className="mt-14 grid gap-8 lg:grid-cols-3">
            {t.home.services.map((s, i) => (
              <Reveal key={s.key} delay={i * 0.08} className="h-full">
                <Link to={pathFor("services")} className="group flex h-full flex-col border border-slate-200 bg-white p-8 transition-all duration-300 hover:border-royal hover:shadow-[8px_8px_0_0_#1D4ED8]" data-testid={`home-service-${s.key}`}>
                  <span className="font-mono-tech text-xs uppercase tracking-[0.3em] text-slate-400">0{i + 1}</span>
                  <h3 className="mt-4 text-xl font-black uppercase tracking-tight text-slate-900">{s.title}</h3>
                  <p className="mt-4 flex-1 text-sm leading-relaxed text-slate-500">{s.text}</p>
                  <span className="mt-6 flex items-center gap-2 font-mono-tech text-xs uppercase tracking-widest text-royal">
                    {t.home.servicesLink.split(" ")[0]} <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>

          <div className="mt-20 border-t border-slate-200 pt-14">
            <SectionHead kicker={t.home.materialsKicker} title={t.home.materialsTitle} testid="home-materials" />
            <Reveal delay={0.1}>
              <div className="mt-8 flex flex-wrap gap-3" data-testid="materials-chips">
                {t.servicesPage.materials.map((m) => (
                  <span key={m} className="border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:border-royal hover:text-royal">
                    {m}
                  </span>
                ))}
              </div>
              <p className="mt-6 text-sm text-slate-500">{t.home.materialsNote}</p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="bg-anthracite-dark" data-testid="process-section">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-8 lg:py-28">
          <SectionHead kicker={t.home.processKicker} title={t.home.processTitle} light testid="process" />
          <div className="mt-14">
            {t.process.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.05}>
                <div className="group grid grid-cols-[64px_1fr] items-start gap-6 border-t border-white/10 py-8 transition-colors last:border-b hover:bg-anthracite sm:grid-cols-[120px_1fr_1fr]" data-testid={`process-step-${i}`}>
                  <span className="font-mono-tech text-3xl font-semibold text-white/15 transition-colors group-hover:text-royal-soft sm:text-5xl">0{i + 1}</span>
                  <h3 className="text-lg font-bold text-white sm:text-xl">{s.title}</h3>
                  <p className="col-start-2 text-sm text-slate-400 sm:col-start-3">{s.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* IMAGE + CTA BAND */}
      <section className="relative overflow-hidden" data-testid="home-cta-section">
        <img src={GEAR_IMG} alt="Präzise gefertigte Zahnräder und Metallbauteile" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-anthracite-dark/85" />
        <div className="relative mx-auto max-w-7xl px-4 py-24 sm:px-8 lg:py-32">
          <Reveal>
            <h2 className="max-w-3xl text-2xl font-black uppercase tracking-tight text-white sm:text-3xl lg:text-5xl" data-testid="home-cta-title">
              {t.home.ctaTitle}
            </h2>
            <p className="mt-5 max-w-xl text-slate-300">{t.home.ctaText}</p>
            <Link
              to={pathFor("inquiry")}
              className="group mt-10 inline-flex items-center gap-3 bg-royal px-8 py-4 text-sm font-bold uppercase tracking-wider text-white transition-colors hover:bg-royal-hover"
              data-testid="home-cta-button"
            >
              {t.home.ctaButton}
              <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
